import { Fragment } from "react";
import Button from "@components/Button/Button";
import {
  COMMON_BUTTON_CSS,
  REMOVE_FOCUS_CSS,
} from "@components/Button/utils/constants";
import { ActionType, ButtonSize } from "@components/Button/utils/types";
import IconImg from "@components/Icon/IconImg";
import { IconTypeEnum } from "@components/Icon/utils/types";
import {
  SHARE_PANEL_LIST,
  TRACKING_OBJECT,
} from "@features/toolbar/ArticleToolbar/utils/constants";
import { ArticleShareToolPopoverProps } from "@features/toolbar/ArticleToolbar/utils/types";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import useSendDataForTracking from "@hooks/useSendDataForTracking";
import { useWindowSize } from "@hooks/useWindowSize";
import { cn } from "@util/helpers";

/**
 * A component that renders a popover for sharing an article.
 */

export default function ArticleShareToolPopover({
  articleTitle,
  shareUrl,
  testId = "article-popover-component",
  buttonRef,
  text,
  className,
  trackingObject,
}: ArticleShareToolPopoverProps): React.ReactElement {
  const { isScreenLG } = useWindowSize();
  const { sendTrackingData } = useSendDataForTracking();

  const handleOnClickSendData = () => {
    if (!trackingObject) return;

    if (typeof window === "undefined") return;

    if (trackingObject?.eventLabel === undefined) {
      trackingObject.eventLabel = window.location.href;
    }

    sendTrackingData(trackingObject);
  };

  const shareOnClick = () => {
    handleOnClickSendData();

    if (isScreenLG) return;

    if (typeof navigator.share === "function" && shareUrl) {
      navigator.share({
        text: articleTitle, // Current page title
        url: shareUrl, // Current page URL
      });
    }
  };

  if (!isScreenLG) {
    return (
      <Button
        actionType={ActionType.actionButton}
        className={cn("aspect-square h-full p-2", className)}
        onClick={shareOnClick}
        testId="article-social-sharing-component"
      >
        <IconImg
          icon={IconTypeEnum.shareLine}
          iconSize={24}
          imgAlt="share icon"
        />
      </Button>
    );
  }

  const sendSocialShareData = (social: string) => {
    if (!social) return;

    if (typeof window === "undefined") return;

    const trackingData = {
      ...TRACKING_OBJECT.socialShare,
      eventAction: social,
      eventLabel: window.location.href,
    };

    sendTrackingData(trackingData);
  };

  return (
    <Popover className={cn("relative block flex-none")} data-testid={testId}>
      <PopoverButton
        ref={buttonRef}
        data-testid="article-share-popover-button"
        className={cn(COMMON_BUTTON_CSS, REMOVE_FOCUS_CSS, "p-2", className)}
        onClick={handleOnClickSendData}
      >
        <IconImg
          icon={IconTypeEnum.shareLine}
          iconSize={24}
          imgAlt="share icon"
        />

        {text ? <p className="ml-2">{text}</p> : null}
      </PopoverButton>

      <Transition>
        <TransitionChild
          as={Fragment}
          enter="transition ease duration-500 transform"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease duration-150 transform"
          leaveFrom="opacity-100 "
        >
          <PopoverPanel
            data-testid="article-popover-panel"
            anchor={{ to: "bottom start" }}
            className={cn(
              "relative z-[100000] overflow-hidden rounded-md bg-white py-3 shadow-[0px_0px_32px_0px_rgba(0,_0,_0,_0.20)]"
            )}
          >
            {SHARE_PANEL_LIST.map((item) => {
              return (
                <Button
                  key={item.text}
                  icon={item.icon}
                  whichSocialMedia={item.whichSocialMedia}
                  actionType={ActionType.shareButton}
                  className="m-0 w-full items-center justify-start gap-3 rounded-none border-none px-6 hover:bg-gray-250"
                  imgAlt={articleTitle}
                  title={articleTitle}
                  shareUrl={shareUrl}
                  text={item.text}
                  buttonSize={ButtonSize.medium}
                  callback={() => {
                    sendSocialShareData(item.whichSocialMedia);
                  }}
                />
              );
            })}
          </PopoverPanel>
        </TransitionChild>
      </Transition>
    </Popover>
  );
}
